<template>
    <div class="alerts-wrapper">
        <Alert
            v-for="alert in alerts"
            :key="alert.id"
            :class="alert.class"
            @closed="removeAlert(alert.id)"
            transition="slide-fade-vertical"
        >
        {{ alert.message }}
        </Alert>
    </div>

    <div id="blacklist">
        <section id="blacklist-form-section">
            <h2 class="text-center"><span class="title">Blacklist Emails</span></h2>
            <form @submit.prevent="addContactToBlacklist">
                <label for="jungo-id">
                    <span>ID Jungo&nbsp;<span class="required">*</span></span>
                    <input id="jungo-id" type="text" placeholder="ID Jungo du contact" v-model="contactJungoID" :class="{'input-error': errors.jungoId}">
                </label>
                
                <label for="first-name">
                    <span>Prénom&nbsp;<span class="required">*</span></span>
                    <input id="first-name" type="text" placeholder="Prénom du contact" v-model="contactFirstName" :class="{'input-error': errors.firstName}">
                </label>

                <label for="last-name">
                    <span>Nom&nbsp;<span class="required">*</span></span>
                    <input id="last-name" type="text" placeholder="Nom du contact" v-model="contactLastName" :class="{'input-error': errors.lastName}">
                </label>

                <label for="email">
                    <span>Email&nbsp;<span class="required">*</span></span>
                    <input id="email" type="email" placeholder="Email du contact" v-model="contactEmail" :class="{'input-error': errors.email}">
                </label>

                <label for="reason">
                    <span>Raison&nbsp;<span class="required">*</span></span>
                    <select id="reason" v-model="reason" :class="{'input-error': errors.reason}">
                        <option :value="null" disabled>Choisisez une raison</option>
                        <option value="Radiation administrative">Radiation administrative</option>
                        <option value="Demandé par le porteur">Demandé par le porteur</option>
                        <option value="Raison non définie">Raison non définie</option>
                    </select>
                </label>

                <button class="danger-button"><i class="material-icons">block</i>&nbsp;Ajouter à la blacklist</button>
            </form>
        </section>

        <hr class="divider">
    
        <section id="blacklist-logs">
            <h2 class="text-center">Blacklisted Logs</h2>

            <input id="search-field" type="text" placeholder="Recherche" v-model="searchInput">

            <table>
                <thead>
                    <tr v-if="blackListedContacts">
                        <th>ID Jungo</th>
                        <th>Nom</th>
                        <th>Email</th>
                        <th>Dernière&nbsp;Modification</th>
                        <th>Motif</th>
                        <th>Action</th>
                    </tr>
                </thead>
    
                <tbody>
                    <tr v-for="contact in blackListedContacts" v-if="blackListedContacts">
                        <!-- Contact Jungo ID -->
                        <td class="fit-width">{{ contact?.jungo_id || 'Inconnu' }}</td>
                        
                        <!-- Contact Name -->
                        <td class="fit-width">
                            <div class="name-field">
                                <p><span class="uppercase">{{ contact.decrypted?.last_name ?? contact.last_name }}&nbsp;</span>{{ contact.decrypted?.first_name ?? contact.first_name }}</p>
                                <button @click="displayEncryption(contact)" class="secondary-button-discret" v-if="userStore.current_user_groups.includes('SUPER_ADMIN')"><i class="material-icons">info</i></button>
                            </div>
                        </td>

                        <!-- Contact E-mail -->
                        <td class="wrap">{{ contact.decrypted?.email ?? contact.email }}</td>

                        <!-- Update date -->
                        <td class="fit-width">
                            {{ new Date(contact.updated_at).toLocaleDateString('fr-FR') }} à {{ new Date(contact.updated_at).toLocaleTimeString('fr-FR') }}
                        </td>
                        
                        <!-- Blacklist Reason -->
                        <td class="fit-width">{{ contact.blacklist_reason }}</td>
                        
                        <!-- Action column -->
                        <td class="fit-width">
                            <button class="danger-button-discret" @click="[confirmUnblock = true , selectedEmail = contact.email]">Débloquer ce contact</button>
                        </td>
                    </tr>
                    
                    <tr v-else-if="!blackListedContacts && loading">
                        <td class="text-center">fetching...</td>
                    </tr>

                    <tr v-else>
                        <td class="text-center">Aucun email trouvé.</td>
                    </tr>
                </tbody>
            </table>
            
            <div class="pagination-display">
                <div>
                    <label for="page_size">
                        Afficher&nbsp;:
                        <select v-model="page_size" id="page_size">
                            <option value="20">20</option>
                            <option value="40">40</option>
                            <option value="60">60</option>
                            <option value="80">80</option>
                            <option value="100">100</option>
                        </select>
                        &nbsp;emails par page.
                    </label>
                </div>

                <div class="pagination">
                    <button @click="previousPage" class="secondary-button-outlined" :disabled="page <= 1 || !blackListedContacts"><i class="material-icons">arrow_back</i>&nbsp;Précédent</button>
                    <div class="page-display">
                        <p>Page: {{ page }}</p>
                        <p><span class="bold">{{ offsetStart }}-{{ offsetEnd }}</span>&nbsp;emails sur&nbsp;<span class="bold">{{ emailsTotal }}</span>&nbsp;affichés</p>
                    </div>
                    <button @click="nextPage" class="secondary-button-outlined" :disabled="page >= max_pages || !blackListedContacts">Suivant&nbsp;<i class="material-icons">arrow_forward</i></button>
                </div>
            </div>
        </section>
    </div>
    
    <Modal v-model="confirmUnblock">
        <aside class="modal-display">
            <h2>Débloquer le contact&nbsp;?</h2>
            <p>Souhaitez-vous réellement débloquer le contact <span class="bold important">{{ selectedEmail }}</span>&nbsp;?</p>
            <div class="grouped-buttons">
                <button class="secondary-button-discret" @click="confirmUnblock = false">Annuler</button>
                <button class="danger-button" @click="deleteFromBlacklist">Débloquer</button>
            </div>
        </aside>
    </Modal>

    <Modal v-model="contactInfoModal">
        <aside class="modal-display fit">
            <h1>ID Jungo&nbsp;: {{ selectedContact.jungo_id || 'Inconnu' }}</h1>
            <table>
                <thead>
                    <tr>
                        <th></th>
                        <th>Decrypted</th>
                        <th>Encrypted</th>
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <th>Nom</th>
                        <td>{{ selectedContact.decrypted?.last_name }}</td>
                        <td>{{ selectedContact.last_name }}</td>
                    </tr>
                    <tr>
                        <th>Prénom</th>
                        <td>{{ selectedContact.decrypted?.first_name }}</td>
                        <td>{{ selectedContact.first_name }}</td>
                    </tr>
                    <tr>
                        <th>E-mail</th>
                        <td>{{ selectedContact.decrypted?.email }}</td>
                        <td>{{ selectedContact.email }}</td>
                    </tr>
                </tbody>
            </table>
        </aside>
    </Modal>
</template>

<script setup>
    import apiClient                            from '@/api';
    import { computed, onMounted, ref, watch }  from 'vue';
    import Modal                                from '@/components/Modal.vue';
    import Alert                                from '@/components/Alert.vue';
    import { useDebounceWatch }                 from '@/composables/useDebounchWatch';
    import { useUserStore }                     from '@/stores/userstore';

    const userStore = useUserStore()

    const blackListedContacts = ref(null)
    const page              = ref(1)
    const page_size         = ref(20)
    const max_pages         = ref(1)
    const emailsTotal       = ref(0)
    const loading           = ref(false)
    const confirmUnblock    = ref(false)
    const selectedEmail     = ref(null)
    const contactJungoID    = ref(null)
    const contactFirstName  = ref(null)
    const contactLastName   = ref(null)
    const contactEmail      = ref(null)
    const reason            = ref(null)
    const submitted         = ref(false)
    const errors            = ref({
        firstName:  false,
        lastName:   false,
        email:      false,
        reason:     false,
        jungoId:    false
    })
    const searchInput       = ref(null)
    const search            = ref(null)
    const contactInfoModal  = ref(false)
    const selectedContact   = ref(null)

    const offsetStart = computed(() => {
        if (!blackListedContacts.value) return 0
        return (page.value - 1) * page_size.value + 1
    })

    const offsetEnd = computed(() => {
        if (!blackListedContacts.value) return 0
        return (page.value - 1) * page_size.value + blackListedContacts.value.length
    })

    async function addContactToBlacklist() {
        submitted.value     = true
        const emailRegex    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

        errors.value = {
            jungoId:    !contactJungoID.value,
            firstName:  !contactFirstName.value,
            lastName:   !contactLastName.value,
            email:      !contactEmail.value || !emailRegex.test(contactEmail.value),
            reason:     !reason.value
        }

        if (Object.values(errors.value).some(Boolean)) return

        try {
            const formData = new FormData()
            formData.append('jungo_id',     contactJungoID.value)
            formData.append('firstName',    contactFirstName.value)
            formData.append('lastName',     contactLastName.value)
            formData.append('email',        contactEmail.value)
            formData.append('reason',       reason.value)
            await apiClient.post('/blacklist_contact/', formData)
            addAlert('Contact blacklisté avec succèss.', 'success-alert')
            submitted.value         = false
            contactJungoID.value    = null
            contactFirstName.value  = null
            contactLastName.value   = null
            contactEmail.value      = null
            reason.value            = null
            errors.value            = {
                jungoId:    false,
                firstName:  false,
                lastName:   false,
                email:      false,
                reason:     false
            }
            fetchBlacklistedEmails()
        } catch (e) {
            addAlert("Échec : Une erreur s'est produite, veuillez réessayer.", 'danger-alert')
            console.error(e)
        }
    }

    async function fetchBlacklistedEmails() {
        loading.value = true
        try {
            blackListedContacts.value = null

            const params = {
                'page':         page.value,
                'page_size':    page_size.value,
                'search':       search.value
            }

            const response = await apiClient.get('/get_blacklisted_contacts/', { params })
            addAlert('Blacklist récupérée avec succèss.', 'success-alert')
            blackListedContacts.value   = response.data.contacts
            max_pages.value             = response.data.pages
            emailsTotal.value           = response.data.count
        } catch (e) {
            addAlert("Échec : Impossible de récupérer les contacts blacklistés, veuillez réessayer.", 'danger-alert')
            console.error(e)
        }
        loading.value = false
    }

    async function deleteFromBlacklist() {
        try {
            const formData = new FormData()
            formData.append('email', selectedEmail.value)

            await apiClient.put('/unblock_blacklisted_contacts/', formData)
            addAlert('Contact debloqué avec succèss.', 'success-alert')
            selectedEmail.value = null
            
            fetchBlacklistedEmails()
        } catch (e) {
            addAlert("Échec : Impossible de débloquer l'utilisateur actuellement.", 'danger-alert')
            console.error(e)
        }

        confirmUnblock.value = false
    }

    function previousPage() {
        if (page.value > 1) page.value--
    }

    function nextPage() {
        if (page.value < max_pages.value) page.value++

    }

    // alert handler start
    const alerts    = ref([])
    let idCounter   = 0

    const addAlert = (message, alertClass) => {
        const existingIndex = alerts.value.findIndex(a => a.message === message)

        // If alert already exists
        if (existingIndex !== -1) {
            const existingAlert = alerts.value[existingIndex]

            // Remove it from its current position
            alerts.value.splice(existingIndex, 1)

            // Force re-render (restart animation & timer)
            idCounter++
            existingAlert.id = idCounter
            existingAlert.class = alertClass

            // Add it back at the top
            alerts.value.unshift(existingAlert)
            return
        }

        // Otherwise create a new alert
        idCounter++

        alerts.value.unshift({
            id: idCounter,
            class: alertClass,
            message: message
        })
    }

    const removeAlert = (id) => {
        alerts.value = alerts.value.filter(a => a.id !== id)
    }
    // alert handler end

    const displayEncryption = async (contact) => {
        contactInfoModal.value  = true
        selectedContact.value   = contact
    }

    useDebounceWatch(searchInput, (newVal) => {
        search.value = newVal
        fetchBlacklistedEmails()
    })

    watch(contactInfoModal, (newVal) => {
        if (newVal === false) {
            selectedContact.value   = null
        }
    })

    watch(page, fetchBlacklistedEmails)

    watch(page_size, () => {
        page.value = 1
        fetchBlacklistedEmails()
    })

    watch(contactJungoID,   (val) => { if (submitted.value) errors.value.jungoId        = !val })
    watch(contactFirstName, (val) => { if (submitted.value) errors.value.firstName      = !val })
    watch(contactLastName,  (val) => { if (submitted.value) errors.value.lastName       = !val })
    watch(contactEmail,     (val) => {
        if (!submitted.value) return
        const emailRegex    = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        errors.value.email  = !val || !emailRegex.test(val)
    })
    watch(reason,           (val) => { if (submitted.value) errors.value.reason         = !val })

    onMounted(fetchBlacklistedEmails)
</script>

<style lang="scss" scoped>
html label,
body label {
    display: block;
    gap: 0;
    align-items: start;
}

#blacklist {
    .pagination-display {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: .5rem;

        margin-block: 1rem;
    }

    .page-display {
        display: flex;
        flex-direction: column;
        align-items: center;

        width: max-content;

        p {
            margin: 0;
        }
    }

    .pagination {
        display: flex;
        justify-content: center;
        gap: 2rem;
    }
    
    .secondary-button-outlined:disabled {
        opacity: .4;
        cursor: not-allowed;
        
        &:hover {
            background: inherit;
            color: #6c757d;
        }
    }
    
    .text-center {
        text-align: center;
    }

    table {
        table-layout: auto;
        width: 100%;

        .fit-width {
            width: 1%;
            white-space: nowrap;
        }

        .name-field {
            display: flex;
            justify-content: space-between;
            align-items: center;
        }
    }
    
    form {
        width: 500px;
        margin: auto;

        display: flex;
        flex-direction: column;
        gap: .5rem;
        justify-content: end;

        label:has( > span:first-child) {
            display: flex;
            flex-direction: column;
        }

        input,
        select {
            width: 100%;

            box-sizing: border-box;
            border: 2px solid transparent;
        }

        button {
            width: fit-content;

            margin-top: .5rem;
            align-self: end;
        }

        .required {
            color: $darkred;
        }

        input.input-error,
        select.input-error {
            border: 2px solid $darkred;
            outline-color: $darkred;
        }
    }
}

#blacklist-logs {
    table {
        width: 100%;
    }

    td.wrap {
        overflow-wrap: anywhere;
        min-width: 12rem;
    }
}

.modal-display {
    width: 450px;

    &.fit {
        width: 100%;
    }
    
    .grouped-buttons {
        display: flex;
        justify-content: end;
        
        gap: 1rem;
    }

    tbody th {
        text-align: start;
        padding-inline: .5rem;
    }
}

.bold {
    font-weight: bold;
}

.important {
    color: $dark-blue;
}

.alerts-wrapper {
    position: fixed;
    top: 0;
    left: 0;

    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    z-index: 9999;

    align-items: center;

    padding-top: 2rem;
    width: 100%;
}

.divider {
    margin: 2rem;
}

.uppercase {
    text-transform: uppercase;
}

#search-field {
    margin-block: 1rem;
}
</style>